// src/utils/coordinateConversion.js

/**
 * Accurate conversion between geographic coordinates (lat/lng) and local Cartesian coordinates (x, z)
 * using the Haversine formula for distance and bearing calculations.
 */

const EARTH_RADIUS_METERS = 6378137; // WGS84 ellipsoid

/**
 * Convert degrees to radians
 */
function toRadians(degrees) {
  return degrees * Math.PI / 180;
}

/**
 * Convert radians to degrees
 */
function toDegrees(radians) {
  return radians * 180 / Math.PI;
}

/**
 * Calculate the Haversine distance between two points on Earth
 * Returns distance in meters
 */
export function haversineDistance(lat1, lng1, lat2, lng2) {
  const φ1 = toRadians(lat1);
  const φ2 = toRadians(lat2);
  const Δφ = toRadians(lat2 - lat1);
  const Δλ = toRadians(lng2 - lng1);

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_METERS * c;
}

/**
 * Calculate bearing from point 1 to point 2
 * Returns bearing in degrees (0-360, where 0 is North)
 */
export function calculateBearing(lat1, lng1, lat2, lng2) {
  const φ1 = toRadians(lat1);
  const φ2 = toRadians(lat2);
  const Δλ = toRadians(lng2 - lng1);

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) -
            Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  
  const θ = Math.atan2(y, x);
  
  return (toDegrees(θ) + 360) % 360;
}

/**
 * Convert lat/lng to local XZ coordinates relative to a center point
 * Uses accurate Haversine distance calculation
 * 
 * @param {number} lng - Longitude of point to convert
 * @param {number} lat - Latitude of point to convert
 * @param {number} centerLng - Longitude of origin/center point
 * @param {number} centerLat - Latitude of origin/center point
 * @returns {{x: number, z: number}} Local coordinates in meters
 * 
 * Coordinate system:
 * - X axis: East (positive) / West (negative)
 * - Z axis: South (positive) / North (negative)
 * - Origin (0,0): centerLat, centerLng
 */
export function lonLatToLocalXZ(lng, lat, centerLng, centerLat) {
  // Calculate distance from center
  const distance = haversineDistance(centerLat, centerLng, lat, lng);
  
  // Calculate bearing from center to point
  const bearing = calculateBearing(centerLat, centerLng, lat, lng);
  
  // Convert to radians for trigonometry
  const bearingRad = toRadians(bearing);
  
  // Convert to local XZ coordinates
  // Bearing 0° (North) → Z negative
  // Bearing 90° (East) → X positive
  // Bearing 180° (South) → Z positive
  // Bearing 270° (West) → X negative
  const x = distance * Math.sin(bearingRad);
  const z = -distance * Math.cos(bearingRad); // Negative because North is -Z
  
  return { x, z };
}

/**
 * LEGACY: Simple approximation (less accurate, for comparison)
 * This is what you were using before - kept for reference
 */
export function lonLatToLocalXZ_Simple(lng, lat, centerLng, centerLat) {
  const Rdeg = 111320; // meters per degree at equator
  const dx = (lng - centerLng) * (Rdeg * Math.cos((centerLat * Math.PI) / 180));
  const dz = (lat - centerLat) * Rdeg;
  return { x: dx, z: dz };
}

/**
 * Convert local XZ coordinates back to lat/lng
 * 
 * @param {number} x - Local X coordinate in meters
 * @param {number} z - Local Z coordinate in meters
 * @param {number} centerLng - Longitude of origin
 * @param {number} centerLat - Latitude of origin
 * @returns {{lng: number, lat: number}} Geographic coordinates
 */
export function localXZToLonLat(x, z, centerLng, centerLat) {
  // Calculate distance from origin
  const distance = Math.sqrt(x * x + z * z);
  
  // Calculate bearing (accounting for Z being negative North)
  const bearingRad = Math.atan2(x, -z);
  const bearing = toDegrees(bearingRad);
  
  // Convert distance and bearing to lat/lng
  const φ1 = toRadians(centerLat);
  const λ1 = toRadians(centerLng);
  const bearingRadPositive = toRadians((bearing + 360) % 360);
  
  const δ = distance / EARTH_RADIUS_METERS; // angular distance
  
  const φ2 = Math.asin(
    Math.sin(φ1) * Math.cos(δ) +
    Math.cos(φ1) * Math.sin(δ) * Math.cos(bearingRadPositive)
  );
  
  const λ2 = λ1 + Math.atan2(
    Math.sin(bearingRadPositive) * Math.sin(δ) * Math.cos(φ1),
    Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2)
  );
  
  return {
    lat: toDegrees(φ2),
    lng: toDegrees(λ2)
  };
}

/**
 * Convert a polygon from lat/lng coordinates to local XZ coordinates
 * 
 * @param {Array<{lng: number, lat: number}>} polygon - Array of lat/lng points
 * @param {number} centerLng - Longitude of origin
 * @param {number} centerLat - Latitude of origin
 * @returns {Array<{x: number, z: number}>} Array of local XZ points
 */
export function polygonToLocalXZ(polygon, centerLng, centerLat) {
  return polygon.map(point => 
    lonLatToLocalXZ(point.lng, point.lat, centerLng, centerLat)
  );
}

/**
 * Calculate the perimeter of a polygon in meters
 * 
 * @param {Array<{x: number, z: number}>} polygon - Array of local XZ points
 * @returns {number} Perimeter in meters
 */
export function calculatePolygonPerimeter(polygon) {
  let perimeter = 0;
  
  for (let i = 0; i < polygon.length; i++) {
    const current = polygon[i];
    const next = polygon[(i + 1) % polygon.length];
    
    const dx = next.x - current.x;
    const dz = next.z - current.z;
    const distance = Math.sqrt(dx * dx + dz * dz);
    
    perimeter += distance;
  }
  
  return perimeter;
}

/**
 * Calculate the area of a polygon in square meters
 * Uses the Shoelace formula
 * 
 * @param {Array<{x: number, z: number}>} polygon - Array of local XZ points
 * @returns {number} Area in square meters
 */
export function calculatePolygonArea(polygon) {
  let area = 0;
  
  for (let i = 0; i < polygon.length; i++) {
    const current = polygon[i];
    const next = polygon[(i + 1) % polygon.length];
    
    area += current.x * next.z - next.x * current.z;
  }
  
  return Math.abs(area) / 2;
}